import { useTimelineStore } from '@/features/editor/deps/timeline-store'
import { useProjectStore } from '@/features/export/deps/projects'
import { getMedia } from '@/infrastructure/storage'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import {
  findCompatibleTrackForItemType,
} from '@/features/editor/deps/timeline-utils'
import { buildDroppedMediaTimelineItem } from '@/features/timeline/utils/dropped-media'
import { createClassicTrack } from '@/features/timeline/utils/classic-tracks'
import type { TextItem, ImageItem } from '@/types/timeline'
import type { TransformProperties } from '@/types/transform'

function getCanvasDimensions(): { width: number; height: number } {
  const project = useProjectStore.getState().currentProject
  if (project?.metadata?.width && project?.metadata?.height) {
    return { width: project.metadata.width, height: project.metadata.height }
  }
  return { width: 1920, height: 1080 }
}

function getFps(): number {
  return useTimelineStore.getState().fps || 30
}

function computePlacementTransform(
  placement: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
  canvasWidth: number,
  canvasHeight: number,
  itemWidth: number,
  itemHeight: number,
  marginX: number,
  marginY: number,
  scale: number,
): TransformProperties {
  const scaledW = Math.round(itemWidth * scale)
  const scaledH = Math.round(itemHeight * scale)

  let x: number
  let y: number

  switch (placement) {
    case 'top-left':
      x = -canvasWidth / 2 + marginX + scaledW / 2
      y = -canvasHeight / 2 + marginY + scaledH / 2
      break
    case 'top-right':
      x = canvasWidth / 2 - marginX - scaledW / 2
      y = -canvasHeight / 2 + marginY + scaledH / 2
      break
    case 'bottom-left':
      x = -canvasWidth / 2 + marginX + scaledW / 2
      y = canvasHeight / 2 - marginY - scaledH / 2
      break
    case 'bottom-right':
      x = canvasWidth / 2 - marginX - scaledW / 2
      y = canvasHeight / 2 - marginY - scaledH / 2
      break
  }

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: scaledW,
    height: scaledH,
    rotation: 0,
    opacity: 0.8,
  }
}

function getDefaultWatermarkEndSeconds(startSeconds: number): number {
  const timeline = useTimelineStore.getState()
  const fps = getFps()
  const maxVideoEndFrame = timeline.items.reduce((maxEnd, item) => {
    if (item.type !== 'video') {
      return maxEnd
    }
    return Math.max(maxEnd, item.from + item.durationInFrames)
  }, 0)
  const fallbackEndSeconds = maxVideoEndFrame > 0 ? maxVideoEndFrame / fps : startSeconds + 120
  return Math.max(startSeconds, fallbackEndSeconds)
}

function overlapsRequestedRange(params: {
  trackId: string
  from: number
  durationInFrames: number
}): boolean {
  const timeline = useTimelineStore.getState()
  const end = params.from + params.durationInFrames
  return timeline.items.some((item) => {
    if (item.trackId !== params.trackId) {
      return false
    }
    const itemEnd = item.from + item.durationInFrames
    return item.from < end && itemEnd > params.from
  })
}

async function findOrCreateVideoTrackForRange(params: {
  createIfNeeded: boolean
  from: number
  durationInFrames: number
}): Promise<{ trackId: string } | null> {
  const timeline = useTimelineStore.getState()
  const compatibleTracks = [...timeline.tracks]
    .filter((track) =>
      findCompatibleTrackForItemType({
        tracks: [track],
        items: timeline.items,
        itemType: 'image',
        preferredTrackId: track.id,
        allowPreferredTrackFallback: false,
      }) !== null,
    )
    .sort((left, right) => left.order - right.order)

  for (const track of compatibleTracks) {
    const hasVideoTrackBelow = compatibleTracks.some((candidate) => candidate.order > track.order)
    if (!hasVideoTrackBelow) {
      continue
    }
    const containsRenderableVisuals = timeline.items.some(
      (item) =>
        item.trackId === track.id &&
        (item.type === 'video' || item.type === 'image' || item.type === 'composition'),
    )
    if (containsRenderableVisuals) {
      continue
    }
    if (
      !overlapsRequestedRange({
        trackId: track.id,
        from: params.from,
        durationInFrames: params.durationInFrames,
      })
    ) {
      return { trackId: track.id }
    }
  }

  if (!params.createIfNeeded) {
    return null
  }

  const topOrder = timeline.tracks.reduce(
    (min, t) => Math.min(min, t.order),
    0,
  )
  const newTrack = createClassicTrack({
    tracks: timeline.tracks,
    kind: 'video',
    order: topOrder - 1,
  })
  timeline.setTracks([...timeline.tracks, newTrack])

  return { trackId: newTrack.id }
}

export async function addBrandWatermark(input: {
  mode: 'text' | 'image'
  text?: string
  imageMediaId?: string
  placement: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  opacity?: number
  scale?: number
  marginX?: number
  marginY?: number
  startSeconds?: number
  endSeconds?: number
  createTrackIfNeeded?: boolean
}): Promise<{
  inserted: boolean
  trackId: string
  itemIds: string[]
}> {
  const canvas = getCanvasDimensions()
  const fps = getFps()
  const createTrack = input.createTrackIfNeeded ?? true
  const scale = input.scale ?? 0.15
  const marginX = input.marginX ?? 20
  const marginY = input.marginY ?? 20
  const opacity = input.opacity ?? 0.8
  const startSeconds = input.startSeconds ?? 0
  const endSeconds = input.endSeconds ?? getDefaultWatermarkEndSeconds(startSeconds)
  const from = Math.round(startSeconds * fps)
  const durationInFrames = Math.max(1, Math.round((endSeconds - startSeconds) * fps))

  // Validate input for each mode
  if (input.mode === 'text' && !input.text) {
    return { inserted: false, trackId: '', itemIds: [] }
  }
  if (input.mode === 'image' && !input.imageMediaId) {
    return { inserted: false, trackId: '', itemIds: [] }
  }

  // Find or create a video track that can preserve the requested placement window.
  const track = await findOrCreateVideoTrackForRange({
    createIfNeeded: createTrack,
    from,
    durationInFrames,
  })
  if (!track) {
    return { inserted: false, trackId: '', itemIds: [] }
  }

  const timeline = useTimelineStore.getState()

  if (input.mode === 'text') {
    // Text watermark
    const watermarkText = input.text!
    const fontSize = Math.round(canvas.height * 0.04)
    const textWidth = Math.round(watermarkText.length * fontSize * 0.6)
    const textHeight = Math.round(fontSize * 1.4)

    const transform = computePlacementTransform(
      input.placement,
      canvas.width,
      canvas.height,
      textWidth,
      textHeight,
      marginX,
      marginY,
      scale,
    )
    transform.opacity = opacity

    const textItem: TextItem = {
      id: crypto.randomUUID(),
      type: 'text',
      trackId: track.trackId,
      from,
      durationInFrames,
      label: `Brand Watermark: ${watermarkText}`,
      text: watermarkText,
      color: '#ffffff',
      fontSize,
      fontFamily: 'Inter',
      fontWeight: 'normal',
      textAlign: 'center',
      lineHeight: 1.2,
      transform,
    }

    timeline.addItem(textItem)

    return {
      inserted: true,
      trackId: track.trackId,
      itemIds: [textItem.id],
    }
  }

  const media = await getMedia(input.imageMediaId!)
  if (!media || !media.mimeType.startsWith('image/')) {
    return { inserted: false, trackId: '', itemIds: [] }
  }

  const resolvedBlobUrl = await mediaLibraryService.getMediaBlobUrl(media.id)
  if (!resolvedBlobUrl) {
    return { inserted: false, trackId: '', itemIds: [] }
  }

  // Image watermark (mode === 'image')
  const imgItemWidth = Math.round(canvas.width * 0.15)
  const imgItemHeight = Math.round(imgItemWidth * 0.5625) // 16:9 ratio fallback

  const transform = computePlacementTransform(
    input.placement,
    canvas.width,
    canvas.height,
    imgItemWidth,
    imgItemHeight,
    marginX,
    marginY,
    scale,
  )
  transform.opacity = opacity

  const imageItem = buildDroppedMediaTimelineItem({
    media,
    mediaId: media.id,
    mediaType: 'image',
    label: media.fileName,
    timelineFps: fps,
    blobUrl: resolvedBlobUrl,
    thumbnailUrl: resolvedBlobUrl,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    placement: {
      trackId: track.trackId,
      from,
      durationInFrames,
    },
  }) as ImageItem
  imageItem.transform = transform

  timeline.addItem(imageItem)

  return {
    inserted: true,
    trackId: track.trackId,
    itemIds: [imageItem.id],
  }
}
